import { ForgotPasswordContactCodeValues } from '@oneview/components/Login/ForgotPasswordContactCodeForm';
import { ForgotPasswordFormValues } from '@oneview/components/Login/FormSchemas';
import { apiConfig, appConfig } from '@oneview/config';
import { TwoFactorLoginStage, TwoFactorSetupStage } from '@oneview/constants/lookups';
import { set2faSetupStage, set2faStage, setAvailable2faOptions } from '@oneview/redux/auth/authActions';
import { selectApiPermissions } from '@oneview/redux/settings/settingSelectors';
import { createQueryFn } from '@oneview/redux/utils';
import { isAdvisor } from '@oneview/utils';
import { isUmaErrorArray } from '@oneview/utils/isUmaErrorArray';
import { LoginStage, TwoFactorAuthenticationType } from '@orbis-systems/orbis-api-clients/orbis';
import {
    SuccessClientResponse,
    UmaB2BSendContactChangeCodeDtoTypeEnum,
    UmaResetPasswordType,
    UmaB2CSendContactChangeCodeDtoTypeEnum,
    UmaB2CUserSettingsSerializer,
    UmaGetWebAuthnLoginChallengeDto,
    UmaMfaCodesDto,
    UmaSendB2BMfaCodeDtoTypeEnum,
    UmaSendB2CMfaCodeDtoTypeEnum,
    UmaSendB2CResetPasswordCodeDtoCodeTypeEnum,
    UmaSendResetPasswordCodeDtoTypeEnum,
    UmaSetB2BUserSettingsDto,
    UmaSetB2CUserSettingsDto,
} from '@orbis-systems/orbis-api-clients/uma';
import { User } from '@orbis-systems/orbis-api-middleware';
import { resetAlertState, resetPersistState, showToast } from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';
import { DataTags } from './dataTags';
import { settingApi } from './settingApi';
import { UmaAuthResponse, WatchmenUserLink } from '@oneview/types';

type LoginArg = {
    username: string;
    password: string;
    type?: UmaSendB2CMfaCodeDtoTypeEnum | UmaSendB2BMfaCodeDtoTypeEnum;
    mfaCodes?: UmaMfaCodesDto;
};

type LoginSsoArg = {
    token: string;
    platform: string;
};

export const authApi = api.injectEndpoints({
    endpoints: (build) => ({
        login: build.mutation({
            queryFn: createQueryFn(
                async (arg: LoginArg | LoginSsoArg, { extra: api, dispatch }): Promise<WatchmenUserLink[] | void> => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const isSsoArg = (value: any): value is LoginSsoArg => {
                        const property: keyof LoginSsoArg = 'token';
                        return Object.prototype.hasOwnProperty.call(value, property);
                    };

                    let response: SuccessClientResponse<{
                        sessionId?: string;
                        twoFactorType?: TwoFactorAuthenticationType;
                        stage?: LoginStage;
                        watchmenUserLinks?: WatchmenUserLink[];
                    }> | null = null;

                    let watchmenUserLinks: WatchmenUserLink[] | undefined;

                    if (isSsoArg(arg)) {
                        if (arg.platform === appConfig.ssoPlatformUma) {
                            api.uma.getApiClient().setAuthData({
                                accessToken: arg.token,
                                refreshToken: '',
                            });
                        } else {
                            response = await api.orbis.authentication.sso(arg.token, arg.platform);
                        }
                        // UMA logic for MFA. umaMfaOptions will be populated if err is requiring MFA code
                    } else if (apiConfig.umaApiUrl) {
                        let mfaCodes: UmaMfaCodesDto | undefined;

                        if (arg.mfaCodes?.email) {
                            mfaCodes = {
                                email: String(arg.mfaCodes.email),
                            };
                        }

                        if (arg.mfaCodes?.phone) {
                            mfaCodes = {
                                phone: String(arg.mfaCodes.phone),
                            };
                        }

                        if (arg.mfaCodes?.totp) {
                            mfaCodes = {
                                totp: String(arg.mfaCodes.totp),
                            };
                        }

                        if (arg.mfaCodes?.yubico_webauthn) {
                            mfaCodes = {
                                yubico_webauthn: arg.mfaCodes.yubico_webauthn,
                            };
                        }

                        if (arg.mfaCodes?.yubico_otp) {
                            mfaCodes = {
                                yubico_otp: arg.mfaCodes.yubico_otp,
                            };
                        }

                        try {
                            const umaLoginResponse = await dispatch(
                                authApi.endpoints.umaLogin.initiate({
                                    username: arg.username,
                                    password: arg.password,
                                    mfaCodes,
                                }),
                            ).unwrap();

                            watchmenUserLinks = umaLoginResponse.login_basic.user.watchmen_user_links;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } catch (error: any) {
                            const errors = error.response.data.errors;
                            let umaMfaOptions;
                            if (isUmaErrorArray(errors)) {
                                umaMfaOptions = errors[0]?.available_options;

                                if (umaMfaOptions) {
                                    dispatch(set2faStage(TwoFactorLoginStage.Incomplete));
                                    dispatch(set2faSetupStage(TwoFactorSetupStage.Complete));
                                    dispatch(setAvailable2faOptions(umaMfaOptions));
                                }

                                errors.forEach((error) => {
                                    dispatch(showToast(error.description || 'Something went wrong'));
                                });
                            }

                            throw error;
                        }
                    } else {
                        response = await api.orbis.authentication.login(arg.username, arg.password);
                    }

                    if (response?.data.sessionId && !apiConfig.umaApiUrl) {
                        await api.orbisSdk.client.setToken(response.data.sessionId);
                        await dispatch(
                            authApi.endpoints.getUserData.initiate(undefined, { forceRefetch: true }),
                        ).unwrap();
                    } else {
                        return watchmenUserLinks;
                    }
                },
            ),
        }),
        watchmenSsoSelectBranch: build.mutation({
            queryFn: createQueryFn(async (branchId: number, { extra: api, dispatch }) => {
                const {
                    data: {
                        data: { token },
                    },
                } = await api.uma.watchmenAuth.generateWatchmenAuthToken({ branch_id: branchId });
                const response = await api.orbis.authentication.sso(token, appConfig.ssoPlatformUma);

                await api.orbisSdk.client.setToken(response.data.sessionId);
                await dispatch(authApi.endpoints.getUserData.initiate(undefined, { forceRefetch: true })).unwrap();
            }),
        }),
        getUserData: build.query({
            queryFn: createQueryFn(async (_: void, { dispatch }) => {
                await dispatch(
                    authApi.endpoints.getMiddlewareTokens.initiate(undefined, { forceRefetch: true }),
                ).unwrap();
                await dispatch(settingApi.endpoints.restoreSettings.initiate()).unwrap();
                await dispatch(authApi.endpoints.updateMiddlewarePermissions.initiate()).unwrap();
                await dispatch(authApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true })).unwrap();
                if (apiConfig.umaApiUrl) {
                    await dispatch(authApi.endpoints.getUmaUserInfo.initiate()).unwrap();
                }
            }),
        }),
        getUmaUserInfo: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                const response = isAdvisor() ? await api.uma.b2bAuth.getCurrent() : await api.uma.b2cAuth.getCurrent();
                return response.data.current_user;
            }),
            providesTags: [DataTags.GetUmaUserInfo],
        }),
        umaLogin: build.mutation({
            queryFn: createQueryFn(
                async (
                    { username, password, mfaCodes }: { username: string; password: string; mfaCodes?: UmaMfaCodesDto },
                    { extra: api },
                ): Promise<UmaAuthResponse> => {
                    let response;
                    if (isAdvisor()) {
                        response = await api.uma.b2bAuth.basicLogin({
                            email: username,
                            password,
                            mfa_codes: mfaCodes,
                            device_id: navigator.userAgent,
                        });
                    } else {
                        response = await api.uma.b2cAuth.basicLogin(
                            {
                                login: username,
                                password,
                                mfa_codes: mfaCodes,
                                device_id: navigator.userAgent,
                            },
                            {
                                FIRM: apiConfig.umaFirmId,
                                BRANCH: apiConfig.umaBranchId,
                            },
                        );
                    }

                    return response.data as unknown as UmaAuthResponse;
                },
            ),
        }),
        logout: build.mutation({
            queryFn: createQueryFn(
                async (_: void, { extra: api, dispatch }) => {
                    try {
                        await api.orbis.authentication.logout();
                        await api.middleware.authentication.invalidate();

                        dispatch(resetAlertState());
                        dispatch(resetPersistState());
                        dispatch(authApi.util.resetApiState());
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        const status = error.response?.status;
                        if (status !== 401 && status !== 440) {
                            throw error;
                        }
                    }
                },
                {
                    pendingMessage: 'Logging out…',
                    alertIfError: true,
                },
            ),
        }),
        getMiddlewareTokens: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                const response = await api.middleware.authentication.authenticate({
                    'Application-Name': 'OneView',
                    'Application-Env': appConfig.appEnv,
                    'Watchmen-Token': api.orbis.getApiClient().getAuthData()?.accessToken || '',
                    'Firm-Id': apiConfig.umaFirmId,
                    'Watchmen-Api': apiConfig.apiUrl,
                });
                await api.middlewareSdk.client.setToken(response.data.accessToken);
            }),
        }),
        getUserInfo: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                const [user] = await Promise.all([api.orbisMiddleware.user.getUserInfo()]);

                return {
                    user: user as User,
                };
            }),
        }),
        updateMiddlewarePermissions: build.mutation({
            queryFn: createQueryFn(async (_: void, { extra: api, getState }) => {
                api.orbisMiddleware.initialize({
                    apiPermissions: selectApiPermissions(getState()),
                });
            }),
        }),
        changePassword: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        currentPassword,
                        newPassword,
                    }: {
                        currentPassword: string;
                        newPassword: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisSdk.client.updatePassword(currentPassword, newPassword);

                    if (response.success) {
                        dispatch(showToast('Your password has been updated'));
                    }
                },
                {
                    pendingMessage: 'Changing password…',
                    alertIfError: true,
                },
            ),
        }),
        sendResetPasswordCode: build.mutation({
            queryFn: createQueryFn(
                async (values: ForgotPasswordContactCodeValues, { extra: api }) => {
                    return isAdvisor()
                        ? api.uma.b2bAuth.sendResetPasswordCode({
                              contact: values.contact,
                              type: values.contactType as UmaSendResetPasswordCodeDtoTypeEnum,
                          })
                        : api.uma.b2cAuth.sendResetPasswordCode(
                              {
                                  auth_form_slug: apiConfig.umaAuthFormSlug,
                                  contact: values.contact,
                                  reset_type: UmaResetPasswordType.PasswordResetByContactCode,
                                  code_type:
                                      values.contactType as unknown as UmaSendB2CResetPasswordCodeDtoCodeTypeEnum,
                              },
                              { FIRM: apiConfig.umaFirmId, BRANCH: apiConfig.umaBranchId },
                          );
                },
                {
                    pendingMessage: 'Sending reset password code…',
                },
            ),
        }),
        resetPassword: build.mutation({
            queryFn: createQueryFn(
                async (
                    // We do not need to send contactType to api, simply used to store values when combining forms ( contact type and contact )
                    values: Omit<ForgotPasswordContactCodeValues, 'contactType'> & ForgotPasswordFormValues,
                    { extra: api, dispatch },
                ) => {
                    const { code, contact, newPassword } = values;
                    const contactInLowerCase = contact.toLowerCase();

                    if (isAdvisor()) {
                        await api.uma.b2bAuth.resetPassword({
                            new_password: newPassword,
                            code,
                            contact: contactInLowerCase,
                        });
                    } else {
                        await api.uma.b2cAuth.resetPassword(
                            {
                                auth_form_slug: apiConfig.umaAuthFormSlug,
                                reset_type: UmaResetPasswordType.PasswordResetByContactCode,
                                new_password: newPassword,
                                ...values,
                                contact: contactInLowerCase,
                            },
                            { FIRM: apiConfig.umaFirmId },
                        );
                    }
                    dispatch(showToast('Password Updated'));
                },
                {
                    pendingMessage: 'Setting Password…',
                },
            ),
        }),
        sendUserMfaCodes: build.mutation({
            queryFn: createQueryFn(
                (
                    {
                        login,
                        type,
                    }: {
                        login: string;
                        type: UmaSendB2CMfaCodeDtoTypeEnum | UmaSendB2BMfaCodeDtoTypeEnum;
                    },
                    { extra: api },
                ) =>
                    isAdvisor()
                        ? api.uma.b2bAuth.sendMfaCode({
                              email: login,
                              type: type as UmaSendB2BMfaCodeDtoTypeEnum,
                              device_id: navigator.userAgent,
                          })
                        : api.uma.b2cAuth.sendMfaCode(
                              {
                                  login,
                                  type: type as UmaSendB2CMfaCodeDtoTypeEnum,
                                  device_id: navigator.userAgent,
                              },
                              {
                                  FIRM: apiConfig.umaFirmId,
                              },
                          ),
                { pendingMessage: true },
            ),
        }),
        getWebAuthLoginChallenge: build.mutation({
            queryFn: createQueryFn(
                async ({ email, user_type }: UmaGetWebAuthnLoginChallengeDto, { extra: api, dispatch }) => {
                    try {
                        const response = await api.uma.authApps.getLoginChallenge({
                            email,
                            user_type,
                        });

                        return response.data;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        dispatch(
                            showToast(
                                error.response.data.errors[0].description ||
                                    'Failed to get WebAuthn register challenge',
                            ),
                        );
                    }
                },
                { pendingMessage: true },
            ),
        }),
        sendContactChangeCode: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        contact,
                        type,
                    }: {
                        contact: string;
                        type: UmaB2BSendContactChangeCodeDtoTypeEnum | UmaB2CSendContactChangeCodeDtoTypeEnum;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        if (isAdvisor()) {
                            await api.uma.b2bUserSettings.sendContactChangeCode({
                                contact,
                                type: type as UmaB2BSendContactChangeCodeDtoTypeEnum,
                            });
                        } else {
                            await api.uma.b2cUserSettings.sendContactChangeCode({
                                contact,
                                type: type as UmaB2CSendContactChangeCodeDtoTypeEnum,
                            });
                        }
                        dispatch(
                            showToast(
                                `A message with a verification code has been sent to your ${type === UmaB2BSendContactChangeCodeDtoTypeEnum.Email ? 'email' : 'phone'}`,
                            ),
                        );
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        dispatch(showToast(error.response.data.errors[0].description));
                        throw error;
                    }
                },
                { pendingMessage: true, alertIfError: false },
            ),
        }),

        verifyEmail: build.mutation({
            queryFn: createQueryFn(
                async (settings: UmaSetB2CUserSettingsDto | UmaSetB2BUserSettingsDto, { extra: api, dispatch }) => {
                    try {
                        if (isAdvisor()) {
                            await api.uma.b2bUserSettings.setUserSettings(settings as UmaSetB2BUserSettingsDto);
                        } else {
                            await api.uma.b2cUserSettings.setUserSettings(settings as UmaB2CUserSettingsSerializer);
                        }
                        dispatch(showToast('Your email has been verified successfully'));
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        dispatch(showToast(error.response.data.errors[0].description || 'Failed to verify email'));
                        throw error;
                    }
                },
                { pendingMessage: true, alertIfError: false },
            ),
            invalidatesTags: [DataTags.GetUmaUserInfo],
        }),
    }),
});

export const {
    useChangePasswordMutation,
    useGetMiddlewareTokensQuery,
    useGetUserDataQuery,
    useLazyGetUserDataQuery,
    useGetUserInfoQuery,
    useLoginMutation,
    useWatchmenSsoSelectBranchMutation,
    useLogoutMutation,
    useUpdateMiddlewarePermissionsMutation,
    useSendResetPasswordCodeMutation,
    useResetPasswordMutation,
    useSendUserMfaCodesMutation,
    useSendContactChangeCodeMutation,
    useVerifyEmailMutation,
    useLazyGetUmaUserInfoQuery,
    useGetWebAuthLoginChallengeMutation,
} = authApi;
